/* eslint-disable react/jsx-no-bind */
import { useCallback, useEffect, useState } from 'react'

import { Plus } from '@gravity-ui/icons'
import { Button, type SortDescriptor } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { UserIcon } from '@/components/icons'
import { ConfirmDeleteDialog } from '@/components/ui/ConfirmDeleteDialog'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageHeader } from '@/components/ui/PageHeader'
import { useUsersStore } from '@/store/users.store'
import { SortDirection } from '@/utils/sorting'

import { UsersTable } from './components/UsersTable'
import UserDialog from './UserDialog'

export default function UsersList() {
  const { t } = useTranslation()
  const { users, deleteUser, isLoading, setPagination, pagination, fetchUsers } = useUsersStore()

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'name',
    direction: 'ascending',
  })

  useEffect(() => {
    setPagination({
      sort: {
        key: sortDescriptor.column as string,
        direction: sortDescriptor.direction === 'ascending' ? SortDirection.ASC : SortDirection.DESC,
      },
    })
  }, [setPagination, sortDescriptor])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | undefined>()
  const [deletingId, setDeletingId] = useState<string | undefined>()

  const handleAdd = useCallback(() => {
    setEditingId(undefined)
    setIsModalOpen(true)
  }, [])

  const handleEdit = (id: string) => {
    setEditingId(id)
    setIsModalOpen(true)
  }

  const handleDeleteTrigger = (id: string) => {
    setDeletingId(id)
    setIsDeleteOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (deletingId) {
      await deleteUser(deletingId)
      setIsDeleteOpen(false)
      setDeletingId(undefined)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingId(undefined)
  }

  return (
    <div className="p-5 space-y-5">
      <PageHeader
        title={t('users.title')}
        subtitle={`${pagination.total} ${t('users.subtitle')}`}
        actions={
          <Button
            onClick={handleAdd}
          >
            <Plus />
            {t('users.add_user')}
          </Button>
        }
      />

      {users.length === 0 && !isLoading ? (
        <EmptyState
          icon={<UserIcon className={'size-8'} />}
          title={t('users.empty_title', 'No users yet')}
          description={t('users.empty_subtitle', 'Add your first user to configure access to the system.')}
        >
          <Button
            size="lg"
            onClick={handleAdd}
          >
            <Plus />
            {t('users.empty_action', 'Add User')}
          </Button>
        </EmptyState>
      ) : (
        <UsersTable
          onEdit={handleEdit}
          onDelete={handleDeleteTrigger}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
        />
      )}

      <UserDialog
        isOpen={isModalOpen}
        onOpenChange={open => !open && closeModal()}
        userId={editingId}
      />

      <ConfirmDeleteDialog
        isOpen={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleDeleteConfirm}
        title={t('common.delete')}
        description={t('users.delete_confirm', 'Are you sure you want to delete this user?')}
        isLoading={isLoading}
      />
    </div>
  )
}
